import { Injectable, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { IonRouterOutlet, NavController } from '@ionic/angular';
import { AuthenticationFacade } from '@symphony-solo/domain/authentication/src/lib/+state/authentication.facade';
import { CompanyConfigFacade } from '@symphony-solo/domain/company-config/src/lib/+state/company-config.facade';
import { OrderFacade } from '@symphony-solo/domain/order/src/lib/+state/order.facade';
import {
  CHOOSE_LOCATION_TABS,
  LOGIN,
  PAYMENT_CONFIRMATION,
  PAYMENT_SUCCESS,
  SELECT_APP,
  SELECT_APP_SIMPLE_TABS,
  SELECT_PRODUCT,
  TABS,
} from '@symphony-solo/shared/utils/src/lib/routes';
import { fromEvent } from 'rxjs';
import { Plugins, Capacitor, App } from '@capacitor/core';

const { Storage } = Plugins;

const DEVICE_INSTALL_UUID_KEY = 'device_install_uuid';
@Injectable()
export abstract class AppBaseComponent {
  @ViewChildren(IonRouterOutlet) protected routerOutlets: QueryList<IonRouterOutlet>;
  constructor(
    protected companyConfigFacade: CompanyConfigFacade,
    protected authenticationFacade: AuthenticationFacade,
    protected orderFacade: OrderFacade,
    protected navCtrl: NavController,
    protected router: Router // private platformIdentityService: PlatformIdentityService
  ) {
    this.initializeApp();
    this.handleHardwareBackButton();
    this.getConfigAterEveryTenMinutes();
  }

  protected async initializeApp(): Promise<void> {
    await App.removeAllListeners();
    await App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.orderFacade.checkOrderStatus();
        this.orderFacade.checkTopUpStatus();
      }
    });
    await this.checkFreshInstallByGeneratedUUID();
  }

  protected async checkFreshInstallByGeneratedUUID() {
    if (Capacitor.isNative) {
      // Retrieve the stored UUID
      const { value: storedUUID } = await Storage.get({ key: DEVICE_INSTALL_UUID_KEY });
      // alert(`Retrieved stored UUID: ${storedUUID}`);

      if (!storedUUID) {
        // Fresh install: Generate a new UUID and store it
        const newUUID = this.generateUUID();
        await Storage.set({ key: DEVICE_INSTALL_UUID_KEY, value: newUUID });
        // alert(`Fresh install detected. Generated new UUID: ${newUUID}`);

        // Perform any "fresh install" logic (e.g., clear basket, reset flags)
        await this.handleFreshInstall();
      } else {
        // alert(`Existing installation detected, UUID: ${storedUUID}`);
      }
    }
  }

  /**
   * Handles actions for a fresh install, like clearing data.
   */
  protected async handleFreshInstall() {
    await this.clearLocalStorageAndIndexedDB();
  }

  /**
   * Clears localStorage, IndexedDB, and Capacitor Storage while preserving the UUID.
   */
  protected async clearLocalStorageAndIndexedDB() {
    if (Capacitor.isNative) {
      localStorage.clear();

      // Clear IndexedDB
      const databases = await (indexedDB as any).databases?.();
      if (databases) {
        for (const db of databases) {
          indexedDB.deleteDatabase(db.name);
        }
      }

      // Preserve UUID before clearing Capacitor Storage
      const { value: uuid } = await Storage.get({ key: DEVICE_INSTALL_UUID_KEY });
      await Storage.clear();

      if (uuid) {
        await Storage.set({ key: DEVICE_INSTALL_UUID_KEY, value: uuid });
      }

      // alert(`Storage cleared. UUID preserved: ${uuid}`);
    }
  }

  protected handleHardwareBackButton(): void {
    let backButtonEvent;
    if (Capacitor.isNative) {
      // Specify the type of the backbutton event for native platforms
      backButtonEvent = fromEvent<CustomEvent>(document, 'backbutton');
    } else {
      // Specify the type of the popstate event for web platforms
      backButtonEvent = fromEvent<PopStateEvent>(window, 'popstate');
    }

    backButtonEvent.subscribe((event) => {
      (event as Event).preventDefault(); // Prevent default back navigation behavior

      this.routerOutlets.forEach(async () => {
        const url = this.router.url;
        if (url.includes(CHOOSE_LOCATION_TABS)) {
          this.navCtrl.navigateRoot('');
          if (Capacitor.isNative) {
            navigator['app'].exitApp(); // Only for Cordova
          }
        }

        const isAuthenticated = await this.authenticationFacade.isAuthenticated$.toPromise();
        if (url.includes(PAYMENT_SUCCESS) && !isAuthenticated) {
          this.navCtrl.navigateRoot(LOGIN); // Redirect to login if not authenticated
          return;
        } else if (url.includes(PAYMENT_SUCCESS)) {
          this.navCtrl.navigateRoot(CHOOSE_LOCATION_TABS); // Redirect to location tabs
          return;
        }

        if (url === `/${TABS}/${SELECT_PRODUCT}` || url === `/${PAYMENT_CONFIRMATION}`) {
          this.authenticationFacade.goHome(); // Navigate to the home page
        } else if (url === `/${SELECT_APP_SIMPLE_TABS}` || url === `/${SELECT_APP}` || url === `/${LOGIN}`) {
          this.navCtrl.navigateRoot('');
          if (Capacitor.isNative) {
            navigator['app'].exitApp(); // Only for Cordova
          }
        } else {
          this.navCtrl.back();
        }
      });
    });
  }

  private getConfigAterEveryTenMinutes() {
    // this.companyConfigFacade.getCompanyConfig();
    // setInterval(() => this.companyConfigFacade.getCompanyConfig(), 600 * 1000);
  }
  private generateUUID(): string {
    return 'fresh_install_001';
  }
}
